import { useEffect, useState, type ReactNode } from 'react';

function useIsMobileUi(): boolean {
  const [v, setV] = useState(() => window.matchMedia('(max-width: 767px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const h = (e: MediaQueryListEvent) => setV(e.matches);
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, []);
  return v;
}
import { statusLabels, statusPalette } from '../../theme/tokens';

/* -------------------------------- Badge -------------------------------- */

export function StatusBadge({ status, label }: { status: string; label?: string }): JSX.Element {
  const [background, color] = statusPalette[status] ?? statusPalette.inactive;
  return (
    <span className="bbm-badge" style={{ background, color }}>
      {label ?? statusLabels[status] ?? status}
    </span>
  );
}

export function Tag({
  children,
  background = '#7FB3D522',
  color = '#33506B',
}: {
  children: ReactNode;
  background?: string;
  color?: string;
}): JSX.Element {
  return (
    <span className="bbm-badge" style={{ background, color }}>
      {children}
    </span>
  );
}

/* ------------------------------- Filtres ------------------------------- */

export interface FilterOption<T extends string> {
  value: T;
  label: string;
}

export function FilterPills<T extends string>({
  options,
  value,
  onChange,
}: {
  options: readonly FilterOption<T>[];
  value: T;
  onChange: (next: T) => void;
}): JSX.Element {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={`bbm-pill${option.value === value ? ' bbm-pill--active' : ''}`}
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function SearchInput({
  value,
  onChange,
  placeholder,
  width = 260,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
  width?: number;
}): JSX.Element {
  return (
    <input
      type="search"
      className="bbm-input"
      style={{ width }}
      placeholder={placeholder}
      value={value}
      aria-label={placeholder}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

/* -------------------------------- États -------------------------------- */

export function EmptyState({
  title,
  body,
  actionLabel,
  onAction,
}: {
  title: string;
  body: string;
  actionLabel?: string;
  onAction?: () => void;
}): JSX.Element {
  return (
    <div className="bbm-state">
      <div className="bbm-state__title">{title}</div>
      <div className="bbm-state__body">{body}</div>
      {actionLabel && onAction ? (
        <button type="button" className="bbm-btn bbm-btn--ghost" onClick={onAction}>
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
}

export function ErrorState({
  status,
  message,
  onRetry,
}: {
  status: number;
  message: string;
  onRetry?: () => void;
}): JSX.Element {
  const denied = status === 401 || status === 403;
  return (
    <div className={`bbm-state${denied ? ' bbm-state--denied' : ' bbm-state--error'}`} role="alert">
      <div className="bbm-state__title">
        {denied ? 'Accès refusé' : status >= 500 ? 'Erreur serveur' : 'Requête refusée'}
      </div>
      <div className="bbm-state__body">
        {message}
        {!denied ? ` (HTTP ${status})` : null}
      </div>
      {!denied && onRetry ? (
        <button
          type="button"
          className="bbm-btn"
          style={{
            padding: '9px 18px',
            borderRadius: 9,
            border: 'none',
            background: '#8A4A44',
            color: '#fff',
            fontSize: 13,
          }}
          onClick={onRetry}
        >
          Réessayer
        </button>
      ) : null}
    </div>
  );
}

export function TableSkeleton({ rows = 6 }: { rows?: number }): JSX.Element {
  return (
    <div className="bbm-panel" aria-busy="true" aria-live="polite">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="bbm-row">
          <div className="bbm-shimmer" style={{ width: 22, height: 14, borderRadius: 4 }} />
          <div className="bbm-shimmer" style={{ width: 180, height: 14, borderRadius: 4 }} />
          <div className="bbm-shimmer" style={{ width: 100, height: 14, borderRadius: 4 }} />
          <div
            className="bbm-shimmer"
            style={{ width: 80, height: 14, borderRadius: 4, marginLeft: 'auto' }}
          />
        </div>
      ))}
    </div>
  );
}

/* ------------------------------ Pagination ----------------------------- */

export function Pager({
  page,
  pageCount,
  total,
  pageSize,
  onChange,
}: {
  page: number;
  pageCount: number;
  total: number;
  pageSize: number;
  onChange: (next: number) => void;
}): JSX.Element {
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  return (
    <div className="bbm-pager">
      <div style={{ fontSize: 13, color: '#8A95A3' }}>
        Affichage {from}-{to} sur {total}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          className="bbm-pager__btn"
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
        >
          Précédent
        </button>
        <button
          type="button"
          className="bbm-pager__btn"
          disabled={page >= pageCount}
          onClick={() => onChange(page + 1)}
        >
          Suivant
        </button>
      </div>
    </div>
  );
}

/* ---------------------------- Barre de masse --------------------------- */

export function BulkBar({
  count,
  children,
  onClear,
}: {
  count: number;
  children: ReactNode;
  onClear: () => void;
}): JSX.Element | null {
  if (count === 0) return null;
  return (
    <div className="bbm-bulkbar">
      <div style={{ fontSize: 13.5, fontWeight: 700 }}>{count} sélectionné(s)</div>
      {children}
      <button type="button" className="bbm-bulkbar__cancel" onClick={onClear}>
        Annuler
      </button>
    </div>
  );
}

/* ---------------------------- Drawer / modale -------------------------- */

function useEscapeKey(onClose: () => void): void {
  useEffect(() => {
    const handler = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);
}

export function Drawer({
  title,
  subtitle,
  onClose,
  children,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
}): JSX.Element {
  useEscapeKey(onClose);
  return (
    <>
      <button type="button" className="bbm-overlay" aria-label="Fermer le panneau" onClick={onClose} />
      <aside className="bbm-drawer" role="dialog" aria-modal="true" aria-label={title}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 18,
            gap: 12,
          }}
        >
          <div>
            <div style={{ fontFamily: "'Baloo 2', sans-serif", fontSize: 19, fontWeight: 700 }}>
              {title}
            </div>
            {subtitle ? (
              <div style={{ fontSize: 13, color: '#8A95A3', marginTop: 2 }}>{subtitle}</div>
            ) : null}
          </div>
          <button
            type="button"
            aria-label="Fermer"
            onClick={onClose}
            style={{
              border: 'none',
              background: '#F4F6F9',
              borderRadius: 99,
              width: 30,
              height: 30,
              color: '#6B7785',
              fontSize: 15,
              flexShrink: 0,
            }}
          >
            ✕
          </button>
        </div>
        {children}
      </aside>
    </>
  );
}

export function Modal({
  title,
  width = 720,
  onClose,
  onSubmit,
  submitLabel = 'Enregistrer',
  busy = false,
  children,
}: {
  title: string;
  width?: number;
  onClose: () => void;
  onSubmit?: () => void;
  submitLabel?: string;
  busy?: boolean;
  children: ReactNode;
}): JSX.Element {
  const isMobile = useIsMobileUi();
  useEscapeKey(onClose);
  return (
    <div
      className="bbm-modal-overlay"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="bbm-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={isMobile ? undefined : { width: `min(${width}px, 92vw)` }}
      >
        <div className="bbm-modal__header">
          {isMobile ? (
            <div style={{ textAlign: 'center', paddingTop: 12, paddingBottom: 2 }}>
              <div style={{ display: 'inline-block', width: 36, height: 4, borderRadius: 2, background: '#DDE2E7' }} />
            </div>
          ) : null}
          <h2 className="bbm-modal__title">{title}</h2>
        </div>

        <div className="bbm-modal__body">
          {children}
        </div>

        <div className="bbm-modal__actions">
          <button
            type="button"
            className="bbm-btn"
            style={{ padding: '10px 18px', borderRadius: 9, fontSize: 14 }}
            onClick={onClose}
          >
            Annuler
          </button>
          {onSubmit ? (
            <button
              type="button"
              className="bbm-btn bbm-btn--primary"
              style={{ padding: '10px 20px', fontSize: 14 }}
              disabled={busy}
              onClick={onSubmit}
            >
              {busy ? 'Enregistrement…' : submitLabel}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function ConfirmModal({
  title,
  body,
  confirmLabel = 'Confirmer',
  danger = false,
  busy = false,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: string;
  confirmLabel?: string;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}): JSX.Element {
  useEscapeKey(onCancel);
  return (
    <div
      className="bbm-modal-overlay"
      role="presentation"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div
        className="bbm-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{ width: 'min(420px, 92vw)' }}
      >
        <div className="bbm-modal__header">
          <h2 className="bbm-modal__title">{title}</h2>
        </div>
        <div className="bbm-modal__body">
          <p style={{ fontSize: 14, color: '#6B7785', marginBottom: 8 }}>{body}</p>
        </div>
        <div className="bbm-modal__actions">
          <button type="button" className="bbm-btn" onClick={onCancel} disabled={busy}>
            Annuler
          </button>
          <button
            type="button"
            className={`bbm-btn ${danger ? 'bbm-btn--danger' : 'bbm-btn--primary'}`}
            style={{ padding: '10px 20px', fontSize: 14 }}
            disabled={busy}
            onClick={onConfirm}
          >
            {busy ? 'En cours…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------- Champs ------------------------------- */

export function TextField({
  label,
  value,
  onChange,
  type = 'text',
  error,
  ...rest
}: {
  label: string;
  value: string | number;
  onChange: (next: string) => void;
  type?: string;
  error?: string;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'>): JSX.Element {
  const id = `field-${label.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <>
      <label className="bbm-label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        type={type}
        className="bbm-field"
        style={{ marginBottom: error ? 6 : 16 }}
        value={value}
        aria-invalid={Boolean(error)}
        onChange={(event) => onChange(event.target.value)}
        {...rest}
      />
      {error ? <div className="bbm-error-text">{error}</div> : null}
    </>
  );
}

export function TextAreaField({
  label,
  value,
  onChange,
  rows = 4,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  rows?: number;
}): JSX.Element {
  const id = `area-${label.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <>
      <label className="bbm-label" htmlFor={id}>
        {label}
      </label>
      <textarea
        id={id}
        className="bbm-field"
        style={{ marginBottom: 16, resize: 'vertical' }}
        rows={rows}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </>
  );
}

export function SelectField({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: readonly string[];
  onChange: (next: string) => void;
}): JSX.Element {
  const id = `select-${label.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <>
      <label className="bbm-label" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        className="bbm-field"
        style={{ marginBottom: 16 }}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </>
  );
}

export function Checkbox({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}): JSX.Element {
  return (
    <input
      type="checkbox"
      checked={checked}
      aria-label={label}
      onChange={onChange}
      style={{ width: 16, height: 16, accentColor: '#5CA989', flexShrink: 0 }}
    />
  );
}

import { RefreshCw } from 'lucide-react';

export function RefreshButton({
  onRefresh,
  loading = false,
}: {
  onRefresh: () => void;
  loading?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      className="bbm-btn bbm-btn-icon"
      onClick={onRefresh}
      disabled={loading}
      title="Actualiser"
      aria-label="Actualiser"
    >
      <RefreshCw size={15} className={loading ? 'bbm-spin' : ''} />
    </button>
  );
}
